import { useAuthContext } from 'contexts/AuthContext';
import type { Patient, Resource } from 'fhir/r4';
import { PROGRAM_TABS, referralOrganizations } from 'fhir-sdoh/common/constants';
import React, { useEffect } from 'react';
import { useState } from 'react';
import {
  Popup,
  Segment,
  Modal,
  Button,
  Menu,
  Input,
  Dropdown,
  Icon,
  Grid,
  type DropdownProps,
} from 'semantic-ui-react';
import type { Option } from 'types';
import { dropdownValue } from 'utils';

import PatientCDO from '../../../models/patient';
import Referrals from '../../common/referrals';
import EditPatient from '../../patient/edit';

import EditCC from './addCC';
import MembersList from './list/allmembers';
import InactiveMembers from './list/inactivemembers';
import MyMembersList from './list/mymembers';
import NewMembersList from './list/newmembers';
import RecentMembersList from './list/recentmembers';




/**
 * SUSPECTED DEFECT: two of these three props are never supplied.
 *
 * The only call site is `components/home/index.tsx`, which renders
 * `<Members onPatientSelected={showNewPatient} />` — nothing else. But this component
 * forwards `props.userEmail` to `EditPatient` and `props.practitionerData` to
 * `EditCC`, so both arrive as `undefined` at those children. They are declared
 * optional here to state that, rather than required (which would misdescribe the call
 * site) — whether the children tolerate `undefined` is their contract to answer.
 */
interface MembersProps {
  onPatientSelected: (patient: Patient) => void;
  practitionerData?: unknown;
  userEmail?: string;
}

function Members(props: MembersProps) {
  const [menuIndex, setMenuIndex] = useState(0);
  const [selectedPayer, setSelectedPayer] = useState('ALL');
  const [activeItem, setActiveItem] = useState('all members');
  const [openCreate, setOpenCreate] = useState(false); //for add new member
  const [openCreateCC, setOpenCreateCC] = useState(false); //for add new CC
  const [notification, setNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState('');
  const [searchString, setSearchString] = React.useState<string>('');
  const [refresh, setRefresh] = useState(false);
  const authInfo = useAuthContext();
  const [selectedEligibilityGroup, setSelectedEligibilityGroup] = useState<string | null>(null);
  const [eligibilityGroupDropdownForSelectedPayor, setEligibilityGroupsDropdownForSelectedPayor] = useState<string[] | null>(null);
  const [popupOpen, setPopupOpen] = React.useState(false);

  const tabMaps = PROGRAM_TABS;
  const payersOptions: Option[] = [{ key: 'ALL', text: 'ALL', value: 'ALL' }].concat(authInfo.payers); //for fan search

  useEffect(() => {
    //if payor is updated, eligibilityGroups dropdown is updated as well
    if ((authInfo?.eligibilityGroups?.length ?? 0) > 0 && selectedPayer && selectedPayer !== 'ALL') {
      // Reads the producer's spelling, not `EligibilityGroup`'s `eligiblityGroups`;
      // hence the assertion. KNOWN_BUGS.md SH-2.
      const selectedPayorEligibilityGroup = (
        authInfo?.eligibilityGroups?.find(
          (eligibilityGroup) => eligibilityGroup.orgId === selectedPayer
        ) as unknown as { eligibilityGroups?: string[] } | undefined
      )?.eligibilityGroups;
      if (selectedPayorEligibilityGroup && selectedPayorEligibilityGroup?.length > 0) {
        setEligibilityGroupsDropdownForSelectedPayor(selectedPayorEligibilityGroup);
        setSelectedEligibilityGroup(selectedPayorEligibilityGroup[0]!);
      } else {
        setEligibilityGroupsDropdownForSelectedPayor(null);
        setSelectedEligibilityGroup(null);
      }
    } else {
      setEligibilityGroupsDropdownForSelectedPayor(null);
      setSelectedEligibilityGroup(null);
    }
  }, [authInfo, selectedPayer]);
  //function instead of directly using setState

  const handleOpen = () => {
    setPopupOpen(true);
  };

  const handleClose = () => {
    setPopupOpen(false);
  };

  // `name` is `string`, not `keyof typeof PROGRAM_TABS`, because this is also passed
  // down to `common/referrals`, which declares the callback as `(name: string) => void`
  // and calls it with its own literal. Narrowing the parameter would break that child's
  // contract, so the key is asserted at the lookup instead. A `name` outside the map
  // yields `undefined` here today; every caller passes one of its keys.
  const handleMenuItemClick = (name: string) => {
    setActiveItem(name);
    setMenuIndex(tabMaps[name as keyof typeof PROGRAM_TABS]);
    setSearchString('');
  };

  const handlePayerSelection = (e: React.SyntheticEvent<HTMLElement>, value: DropdownProps) => {
    if (selectedPayer !== value.value) {
      // `DropdownProps['value']` is wider than the `useState('ALL')` it feeds; the
      // options are `payersOptions`, whose `value` is a `string`.
      setSelectedPayer(value.value as string);
    }
  };
  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value === ' ' ? '' : e.target.value);
  };

  // For Update Patient Details modal window
  const toggleModal = (flag: boolean, fhirPatient?: Resource) => {
    setOpenCreate(flag);
    setNotificationMsg('New patient has been added successfully...');

    if (fhirPatient !== undefined && fhirPatient) {
      setNotification(true);
      setRefresh(true);
    }
  };

  // For Update practitioner Details modal window
  const togglePractitionerModal = (flag: boolean) => {
    setOpenCreateCC(flag);
  };

  const handlePatientSelect = (selectedPatient: Patient) => {
    props.onPatientSelected(selectedPatient);
  };

  return (
    <div>
      <Menu pointing>
        <Popup
          flowing
          trigger={
            <Dropdown
              defaultValue={payersOptions[0]!.value}
              pointing
              style={{ zIndex: '99999' }}
              className="link item"
              options={payersOptions}
              onChange={(e, value) => handlePayerSelection(e, value)}
            />
          }
          content="Payer Organization"
          on="hover"
        />
        {eligibilityGroupDropdownForSelectedPayor && (
          <Menu.Item>
            <Dropdown
              options={eligibilityGroupDropdownForSelectedPayor?.map((eligibilityGroup) => ({
                key: eligibilityGroup,
                text: eligibilityGroup,
                value: eligibilityGroup,
              }))}
              {...dropdownValue(selectedEligibilityGroup)}
              onChange={(event, { value }) => setSelectedEligibilityGroup(value as string)}
            />
          </Menu.Item>
        )}
        <Menu.Item
          key="all members"
          name="all members"
          active={activeItem === 'all members'}
          onClick={() => handleMenuItemClick('all members')}
        />
        <Menu.Item
          key="my members"
          name="my members"
          disabled={selectedPayer === 'ALL'}
          active={activeItem === 'my members'}
          onClick={() => handleMenuItemClick('my members')}
        />
        <Menu.Item
          key="recent members"
          name="recent members"
          disabled={selectedPayer === 'ALL'}
          active={activeItem === 'recent members'}
          onClick={() => handleMenuItemClick('recent members')}
        />
        <Menu.Item
          key="unassigned members"
          name="unassigned members"
          disabled={selectedPayer === 'ALL'}
          active={activeItem === 'unassigned members'}
          onClick={() => handleMenuItemClick('unassigned members')}
        />
        <Menu.Item
          key="inactive members"
          name="inactive members"
          disabled={selectedPayer === 'ALL'}
          active={activeItem === 'inactive members'}
          onClick={() => handleMenuItemClick('inactive members')}
        />
        {referralOrganizations.includes(selectedPayer) && (
          <Menu.Item
            key="referrals"
            name="referrals"
            active={activeItem === 'referrals'}
            onClick={() => handleMenuItemClick('referrals')}
          />
        )}
        <Menu.Menu key="member props" position="right">
          <Menu.Item key="search">
            <Popup
              content="Enter Text and Click Search Icon"
              trigger={
                <Input
                  value={searchString}
                  icon={<Icon size="large" name="search" color="blue" link />}
                  onChange={onSearchInputChange}
                  placeholder="Search Members"
                />
              }
            />
          </Menu.Item>
          {activeItem !== 'referrals' && selectedPayer !== 'ALL' && (
            <>
              <Menu.Item key="add">
                <>
                  <Popup
                    flowing
                    trigger={<Button color="green">Add</Button>}
                    content={
                      <Grid divided>
                        <Grid.Column width={9}>
                          <Button
                            color="green"
                            onClick={() => {
                              setOpenCreate(true);
                              setOpenCreateCC(false);
                              handleClose();
                            }}
                          >
                            Member
                          </Button>
                        </Grid.Column>{' '}
                        <Grid.Column width={2}>
                          <Button
                            color="green"
                            onClick={() => {
                              setOpenCreateCC(true);
                              setOpenCreate(false);
                              handleClose();
                            }}
                          >
                            CC
                          </Button>
                        </Grid.Column>
                      </Grid>
                    }
                    open={popupOpen}
                    onClose={handleClose}
                    onOpen={handleOpen}
                    on="click"
                  />
                </>
              </Menu.Item>
            </>
          )}
        </Menu.Menu>
      </Menu>
      <Segment>
        {selectedPayer && selectedPayer.length > 0 && menuIndex === PROGRAM_TABS['all members'] && (
          <MembersList
            payer={selectedPayer}
            searchString={searchString}
            selectedEligibilityGroup={selectedEligibilityGroup}
            onPatientSelect={handlePatientSelect}
            refresh={refresh}
            setRefresh={setRefresh}
          />
        )}
        {selectedPayer && selectedPayer.length > 0 && menuIndex === PROGRAM_TABS['my members'] && (
          <MyMembersList
            payer={selectedPayer}
            searchString={searchString}
            selectedEligibilityGroup={selectedEligibilityGroup}
            onPatientSelect={handlePatientSelect}
            refresh={refresh}
            setRefresh={setRefresh}
          />
        )}
        {selectedPayer && selectedPayer.length > 0 && menuIndex === PROGRAM_TABS['recent members'] && (
          <RecentMembersList
            payer={selectedPayer}
            searchString={searchString}
            selectedEligibilityGroup={selectedEligibilityGroup}
            onPatientSelect={handlePatientSelect}
            refresh={refresh}
            setRefresh={setRefresh}
          />
        )}
        {selectedPayer && selectedPayer.length > 0 && menuIndex === PROGRAM_TABS['unassigned members'] && (
          <NewMembersList
            payer={selectedPayer}
            searchString={searchString}
            selectedEligibilityGroup={selectedEligibilityGroup}
            onPatientSelect={handlePatientSelect}
            refresh={refresh}
            setRefresh={setRefresh}
          />
        )}
        {selectedPayer && selectedPayer.length > 0 && menuIndex === PROGRAM_TABS['inactive members'] && (
          <InactiveMembers
            payer={selectedPayer}
            searchString={searchString}
            selectedEligibilityGroup={selectedEligibilityGroup}
            onPatientSelect={handlePatientSelect}
            refresh={refresh}
            setRefresh={setRefresh}
          />
        )}
        {selectedPayer && selectedPayer.length > 0 && menuIndex === PROGRAM_TABS['referrals'] && (
          <Referrals
            org={selectedPayer}
            searchString={searchString}
            onPatientSelect={handlePatientSelect}
            activeItem={activeItem}
            menuIndex={menuIndex}
            handleMenuItemClick={handleMenuItemClick}
          />
        )}
      </Segment>
      {/* Create patient modal window calling. Pass all the required parameters */}
      {openCreate && (
        <EditPatient
          patientId=""
          patient={PatientCDO}
          organization={selectedPayer}
          operation={'Add'}
          open={openCreate}
          userEmail={props.userEmail}
          toggleModal={toggleModal}
          careteamStatus="UN-ASSIGNED"
          selectedEligibilityGroup={selectedEligibilityGroup}
        />
      )}
      {openCreateCC && (
        <EditCC
          organization={selectedPayer}
          operation={'Add'}
          open={openCreateCC}
          toggleModal={togglePractitionerModal}
          practitionersData={props.practitionerData}
          setNotificationMsg={setNotificationMsg}
          setNotification={setNotification}
        />
      )}
      {notification && (
        <Modal
          open={notification}
          size="mini"
          content={notificationMsg}
          onActionClick={() => setNotification(false)}
          actions={[{ key: 'done', icon: 'check', content: 'OK', positive: true }]}
        ></Modal>
      )}
    </div>
  );
}

export default Members;
